import { cn } from '@foundation/utils/cn';
import { ButtonHTMLAttributes, ReactNode, forwardRef } from 'react';

export type HeadlessIconButtonProps = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'children'
> & {
  readonly icon: ReactNode;
  readonly isLoading?: boolean;
  /** Icon-only buttons have no visible label; an accessible name is required. */
  readonly 'aria-label': string;
};

function HeadlessIconButtonComponent(
  {
    icon,
    className,
    disabled,
    isLoading = false,
    type = 'button',
    ...buttonProps
  }: HeadlessIconButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>,
) {
  const isDisabled = disabled || isLoading;

  return (
    <button
      {...buttonProps}
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={isLoading || undefined}
      data-loading={isLoading ? '' : undefined}
      data-disabled={isDisabled ? '' : undefined}
      className={cn(
        'inline-grid grid-cols-1 grid-rows-1 place-items-center shrink-0 cursor-pointer disabled:cursor-not-allowed',
        className,
      )}
    >
      {icon}
    </button>
  );
}

export const HeadlessIconButton = forwardRef(HeadlessIconButtonComponent);
